/**
 * Client-side crash capture module.
 *
 * Registers `window.onerror` and `window.onunhandledrejection` handlers
 * on import (side-effect). Exports the `<ErrorBoundary>` React component
 * and the `reportCrash` function for programmatic reporting.
 *
 * Architecture refs:
 *   §4.30 — crash capture
 *
 * Feature plan refs:
 *   .plan/features/2026-04-24-admin-beta-feedback.md §4.7 — client hooks spec
 *
 * Design constraints:
 *   - Fire-and-forget: crash reports never block UI rendering.
 *   - Debounce + dedupe: identical crashes within a 10-second window are
 *     collapsed to a single report.
 *   - keepalive: `fetch` uses `keepalive: true` so the request survives an
 *     unload event (e.g. a crash that triggers a page teardown).
 *   - Idempotent registration: calling this module twice (e.g. HMR) does not
 *     register duplicate handlers.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface CrashReport {
  message: string;
  stack: string;
  source: string;
  line?: number;
  col?: number;
  url: string;
  ua: string;
  releaseSha: string;
  ts: number;
}

// ── Deduplication window ──────────────────────────────────────────────────────

const DEDUPE_WINDOW_MS = 10_000;

/** Map of crash fingerprint → expiry timestamp (Date.now() + DEDUPE_WINDOW_MS). */
const seenCrashes = new Map<string, number>();

/**
 * Build a deduplication fingerprint for a crash.
 * Keyed on `message + first stack frame` — two crashes with the same root
 * message and origin point are considered identical within the window.
 */
function crashFingerprint(message: string, stack: string): string {
  const firstFrame =
    stack
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)[0] ?? "";
  return `${message.slice(0, 200)}|${firstFrame.slice(0, 200)}`;
}

/**
 * Returns `true` if this crash is a duplicate within the deduplication window.
 * Records the crash if it is not a duplicate.
 */
function isDuplicate(message: string, stack: string): boolean {
  const key = crashFingerprint(message, stack);
  const now = Date.now();

  // Sweep expired entries (keep the map from growing unboundedly).
  for (const [k, expiry] of seenCrashes) {
    if (expiry <= now) seenCrashes.delete(k);
  }

  if (seenCrashes.has(key)) return true;
  seenCrashes.set(key, now + DEDUPE_WINDOW_MS);
  return false;
}

// ── Release SHA ───────────────────────────────────────────────────────────────

/**
 * Read the release SHA from `window.__PROOF_RELEASE_SHA` if injected at
 * build time (e.g. via Vite define). Gracefully returns empty string if not set.
 */
function getReleaseSha(): string {
  try {
    return (
      (
        window as Window & {
          __PROOF_RELEASE_SHA?: string;
        }
      ).__PROOF_RELEASE_SHA ?? ""
    );
  } catch {
    return "";
  }
}

// ── Fire-and-forget POST ──────────────────────────────────────────────────────

const INGEST_URL = "/rpc/ingest/crash";

/**
 * Post a crash report to the server. Fire-and-forget.
 *
 * Uses `keepalive: true` so the request survives a page unload.
 * Never throws — if the fetch fails it is silently swallowed.
 */
export function reportCrash(report: CrashReport): void {
  // Dedupe: drop identical crashes within the 10-second window.
  if (isDuplicate(report.message, report.stack)) return;

  // Fire and forget — no await, no retry.
  try {
    void fetch(INGEST_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // oRPC expects { json: <input> } per the oRPC fetch client protocol.
      body: JSON.stringify({
        json: {
          message: report.message,
          stack: report.stack,
          source: report.source,
          line: report.line,
          col: report.col,
          url: report.url,
          ua: report.ua,
          releaseSha: report.releaseSha,
          ts: report.ts,
        },
      }),
      keepalive: true,
      // eslint-disable-next-line no-restricted-syntax -- deliberately silent: this IS the crash reporter; logging its own failure risks a secondary crash / feedback loop (Rule 8 documented degradation).
    }).catch(() => {
      // Intentionally silent — crash reporting must never cause secondary crashes.
    });
    // eslint-disable-next-line no-restricted-syntax -- deliberately silent: crash-report transport must never throw back into the crashing context (Rule 8 documented degradation).
  } catch {
    // Intentionally silent.
  }
}

// ── Handler registration ──────────────────────────────────────────────────────

let handlersRegistered = false;

/**
 * Register the global crash handlers.
 *
 * Idempotent — calling multiple times (e.g. HMR) only registers once.
 * Called as a side-effect of importing this module.
 */
export function registerCrashHandlers(): void {
  if (handlersRegistered) return;
  handlersRegistered = true;

  window.addEventListener("error", (event: ErrorEvent) => {
    reportCrash({
      message: event.message ?? "(no message)",
      stack: event.error instanceof Error ? (event.error.stack ?? "") : "",
      source: event.filename ?? "",
      line: event.lineno,
      col: event.colno,
      url: window.location.href,
      ua: navigator.userAgent,
      releaseSha: getReleaseSha(),
      ts: Date.now(),
    });
  });

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    const reason = event.reason as unknown;
    const message =
      reason instanceof Error
        ? reason.message
        : typeof reason === "string"
          ? reason
          : "(unhandled rejection)";
    const stack = reason instanceof Error ? (reason.stack ?? "") : "";

    reportCrash({
      message,
      stack,
      source: "",
      url: window.location.href,
      ua: navigator.userAgent,
      releaseSha: getReleaseSha(),
      ts: Date.now(),
    });
  });
}

// Register handlers as a module side-effect.
// This is intentionally at module load time, not inside a React component,
// so crashes before React mounts are still captured.
if (typeof window !== "undefined") {
  registerCrashHandlers();
}

// ── ErrorBoundary React component ─────────────────────────────────────────────

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * React error boundary for the proof. SPA.
 *
 * Mount OUTSIDE the router (in entry.client.tsx) so a route-level crash
 * does not unmount the entire shell.
 *
 * On catch: fires a fire-and-forget crash report to `ingest.crash`, then
 * renders a minimal fallback.
 *
 * Fallback copy: "Something broke. The error has been logged."
 *   - Sentence-case, terminal period, no exclamation.
 *   - No `we`/`us`/`our` per branding.md §5.5.
 *   - No `Oops!`, no `Sorry!` per branding.md §5.2.1.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    reportCrash({
      message: error.message ?? "(no message)",
      stack: error.stack ?? "",
      source: info.componentStack ?? "",
      url: typeof window !== "undefined" ? window.location.href : "",
      ua: typeof navigator !== "undefined" ? navigator.userAgent : "",
      releaseSha: getReleaseSha(),
      ts: Date.now(),
    });
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          style={{
            // Hairline-bordered card matching the design token pattern.
            // Using inline styles here so the fallback renders even if the CSS
            // bundle failed to load (e.g. a parse error in the stylesheet).
            margin: "2rem auto",
            maxWidth: "480px",
            padding: "1.5rem",
            border: "1px solid var(--line)",
            borderRadius: "4px",
            fontFamily: "var(--font-sans)",
            fontSize: "0.875rem",
            color: "var(--muted)",
            lineHeight: "1.5",
          }}
        >
          Something broke. The error has been logged.
        </div>
      );
    }
    return this.props.children;
  }
}
